import React, { useState, useEffect } from "react"
import ReactDOM from "react-dom"

declare global {
  type ManagedModalProvider = {
    modals: ManagedModal[]
    setModals: (fn: (modals: ManagedModal[]) => ManagedModal[]) => void
  }

  type ManagedModal = {
    key: string
    $root: HTMLDivElement
    Component: React.ComponentType
    props: any

    setComponent: (content: React.ComponentType, props: any) => void
    setProps: (props: any) => void
    destroy(): void
  }

  interface Window {
    MANAGED_MODALS: {
      registerProvider: (provider: ManagedModalProvider) => () => void
      providers: ManagedModalProvider[]
      create(): ManagedModal
    }
  }
}

function ensureManagedModals() {
  if (window.MANAGED_MODALS) {
    return
  }

  window.MANAGED_MODALS = {
    providers: [] as ManagedModalProvider[],
    registerProvider(provider: ManagedModalProvider) {
      window.MANAGED_MODALS.providers.push(provider)

      return () => {
        window.MANAGED_MODALS.providers = window.MANAGED_MODALS.providers.filter(
          p => p !== provider,
        )
      }
    },
    create() {
      const provider = window.MANAGED_MODALS.providers.at(-1)
      if (!provider) {
        throw new Error("No ManagedModalProvider registered")
      }
      const $root = document.createElement("div")
      document.body.appendChild($root)
      const modal: ManagedModal = {
        key: Math.random().toString(36),
        $root,
        Component: () => null,
        props: null,

        setComponent(content, props) {
          modal.Component = content
          modal.props = props
          provider.setModals(modals => [...modals])
        },
        setProps(props) {
          modal.props = props
          provider.setModals(modals => [...modals])
        },
        destroy() {
          provider.setModals(modals => modals.filter(m => m.key !== modal.key))
          document.body.removeChild($root)
        },
      }
      provider.setModals(modals => [...modals, modal])
      return modal
    },
  }
}

export function ManagedModalProvider() {
  const [modals, setModals] = useState<ManagedModal[]>([])
  useEffect(() => {
    ensureManagedModals()

    return window.MANAGED_MODALS.registerProvider({
      modals,
      setModals,
    })
  }, [])

  return (
    <React.Fragment>
      {modals.map(modal =>
        ReactDOM.createPortal(<modal.Component key={modal.key} {...modal.props} />, modal.$root),
      )}
    </React.Fragment>
  )
}
